import { api } from "../api.js";
import { fmt, t } from "../i18n.js";
import { useMe } from "../shared.jsx";
import { Due, Loader, PriorityTag, Progress, StatusPill, useLoad } from "../ui.jsx";

export function TaskRow({ task, extra }) {
  return (
    <a className="row" href={`#/tasks/${task.id}`}>
      <div className="row-main">
        <div className="row-title"><span className="ref">{task.ref}</span>{task.title}<PriorityTag priority={task.priority} /></div>
        <div className="row-sub">{task.project_name}{task.city ? ` — ${task.city}` : ""}{extra}</div>
      </div>
      <div className="row-side"><StatusPill status={task.status} /><Due task={task} /></div>
    </a>
  );
}

function Section({ title, count, children, empty }) {
  return (
    <section className="panel">
      <h2>{title}{count != null && <span className="count">{count}</span>}</h2>
      {count === 0 ? <p className="muted">{empty}</p> : children}
    </section>
  );
}

export function Dashboard() {
  const me = useMe();
  const state = useLoad(() => api.get("/dashboard"), []);
  return (
    <div className="page">
      <header className="page-head">
        <h1>مرحبًا، {me.name}</h1>
        <p className="muted">{fmt.date(new Date())}</p>
      </header>
      <Loader state={state}>{(d) => (
        <>
          {d.manager && <ManagerBoard m={d.manager} />}
          <div className="grid-2">
            <Section title="مهام اليوم" count={d.employee.today.length} empty="لا توجد مهام مستحقة اليوم.">
              <div className="list">{d.employee.today.map((x) => <TaskRow key={x.id} task={x} />)}</div>
            </Section>
            <Section title="المعادة للتعديل" count={d.employee.returned.length} empty="لا توجد مهام معادة.">
              <div className="list">{d.employee.returned.map((x) => <TaskRow key={x.id} task={x} />)}</div>
            </Section>
            <Section title="المواعيد الأقرب" count={d.employee.upcoming.length} empty="لا توجد مواعيد قادمة.">
              <div className="list">{d.employee.upcoming.map((x) => <TaskRow key={x.id} task={x} />)}</div>
            </Section>
            <Section title="طلباتي المفتوحة" count={d.employee.open_requests.length}
                     empty={d.employee.awaiting_review ? `${d.employee.awaiting_review} مهمة بانتظار المراجعة، ولا توجد طلبات عوائق مفتوحة.` : "لا توجد طلبات مفتوحة."}>
              <div className="list">{d.employee.open_requests.map((o) => (
                <a key={o.id} className="row" href={`#/tasks/${o.task_id}`}>
                  <div className="row-main"><div className="row-title"><span className="ref">{o.ref}</span>{o.title}</div>
                    <div className="row-sub">عائق: {o.reason_label} — {fmt.relative(o.created_at)}</div></div>
                  <span className="pill pill-blocked">بانتظار القرار</span>
                </a>))}</div>
            </Section>
          </div>
        </>
      )}</Loader>
    </div>
  );
}

function ManagerBoard({ m }) {
  return (
    <>
      <div className="kpis">
        <a className="kpi" href="#/tasks?overdue=1"><b className={m.overdue.length ? "late" : ""}>{m.overdue.length}</b>مهام متأخرة</a>
        <a className="kpi" href="#/approvals"><b>{m.approvals.length}</b>بانتظار الاعتماد</a>
        <a className="kpi" href="#/approvals"><b>{m.obstacles.length}</b>عوائق بانتظار القرار</a>
        <a className="kpi" href="#/projects"><b>{m.projects.length}</b>مشاريع جارية ومخططة</a>
      </div>
      <div className="grid-2">
        <Section title="المتأخرات" count={m.overdue.length} empty="لا توجد مهام متأخرة.">
          <div className="list">{m.overdue.map((x) => <TaskRow key={x.id} task={{ ...x, overdue: true }} extra={` — ${x.assignee_name}`} />)}</div>
        </Section>
        <Section title="طلبات الاعتماد" count={m.approvals.length} empty="لا توجد مهام بانتظار المراجعة.">
          <div className="list">{m.approvals.map((x) => <TaskRow key={x.id} task={x} extra={` — ${x.assignee_name}`} />)}</div>
        </Section>
        <Section title="تقدم المشاريع" count={m.projects.length} empty="لا توجد مشاريع جارية.">
          <div className="list">{m.projects.map((p) => (
            <a key={p.id} className="row" href={`#/projects/${p.id}`}>
              <div className="row-main"><div className="row-title"><span className="ref">{p.ref_code}</span>{p.name}</div>
                <Progress value={p.progress.percent} label={"إنجاز " + p.name} /></div>
              <span className="pill">{t("projectStatus." + p.status)}</span>
            </a>))}</div>
        </Section>
        <Section title="حجم العمل لكل فريق" count={m.team_workload.length} empty="لا توجد مهام مرتبطة بفرق.">
          <table className="table">
            <thead><tr><th>الفريق</th><th>مهام مفتوحة</th><th>الوزن المفتوح</th><th>متأخرة</th></tr></thead>
            <tbody>{m.team_workload.map((w) => (
              <tr key={w.id}><td><a href={`#/teams/${w.id}`}>{w.name}</a></td><td>{w.open_tasks}</td>
                <td>{w.open_weight}</td><td className={w.overdue ? "late" : ""}>{w.overdue}</td></tr>))}</tbody>
          </table>
        </Section>
      </div>
    </>
  );
}
